import React, { useCallback, memo } from 'react';
import {
  FormControl,
  FormGroup,
  FormControlLabel,
  Box,
  Grid,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import { BaseQuestion } from '../../../features/variety-planner/types';
import AppTooltip from '../AppTooltip';
import AppCheckbox from '../AppCheckbox';
import { TranslationKeys } from '../../../../i18n/locales/defaultResources';
import { CropConnectLanguages } from '../../../../services/cropConnectApiClient';

interface MultipleChoiceQuestionProps {
  question: BaseQuestion;
  values?: string[];
  onChange: (values: string[]) => void;
}

const QuestionHeader = styled(Box)(() => ({
  marginBottom: 1,
}));

const MultipleChoiceQuestion: React.FC<MultipleChoiceQuestionProps> = ({
  question,
  values,
  onChange,
}) => {
  //Forced to DE lng, not edtiable in translation modal as per business requirement on this component
  const { t } = useTranslation();

  const questionKey =
    `varietyPlanner_${question.tag}` as keyof typeof TranslationKeys;
  const tooltipKey =
    `varietyPlanner_${question.tag}_Info` as keyof typeof TranslationKeys;

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
      const isChecked = event.target.checked;
      let newValues: string[];

      if (!values || !values.length) {
        newValues = [value];
        onChange(newValues);
        return;
      }

      if (isChecked) {
        newValues = [...values, value];
      } else {
        newValues = values.filter((v) => v !== value);
      }

      onChange(newValues);
    },
    [values, onChange]
  );

  return (
    <Box>
      <QuestionHeader>
        <Typography variant="appH5" color="questionsForm.formLabel">
          {t(questionKey as TranslationKeys, {
            lng: CropConnectLanguages.DeDE,
          })}
        </Typography>
        {question.hasInfo && (
          <AppTooltip
            title={t(tooltipKey as TranslationKeys, {
              lng: CropConnectLanguages.DeDE,
            })}
            tooltipContentSx={{ marginLeft: 0.75 }}
          />
        )}
      </QuestionHeader>

      <FormControl component="fieldset">
        <FormGroup>
          <Grid container>
            {question.options &&
              question.options
                .sort(
                  (firstOption, secondOption) =>
                    (firstOption.order as number) -
                    (secondOption.order as number)
                )
                .map((option) => {
                  const optionKey =
                    `varietyPlanner_${question.tag}_${option.tag?.toUpperCase()}` as keyof typeof TranslationKeys;

                  return (
                    <Grid item xs={12} sm={8} key={option.id}>
                      <FormControlLabel
                        labelPlacement="start"
                        sx={{
                          marginLeft: 0,
                          marginRight: 0,
                          display: 'flex',
                          justifyContent: 'space-between',
                          width: '70%',
                        }}
                        control={
                          <AppCheckbox
                            checked={
                              values?.includes(option.id as string) || false
                            }
                            onChange={(event) =>
                              handleChange(event, option.id as string)
                            }
                          />
                        }
                        label={
                          <Typography
                            variant="appBodyMedium"
                            component="p"
                            color="questionsForm.formGrey"
                            sx={{ wordBreak: 'break-word' }}
                          >
                            {t(optionKey as TranslationKeys, {
                              lng: CropConnectLanguages.DeDE,
                            })}
                          </Typography>
                        }
                      />
                    </Grid>
                  );
                })}
          </Grid>
        </FormGroup>
      </FormControl>
    </Box>
  );
};

export default memo(MultipleChoiceQuestion);
